/**
 * Analytics consent banner (GDPR-oriented).
 *
 * Renamed from CookieConsent — dev URLs like `/src/components/CookieConsent.jsx` are often
 * blocked by browser extensions (ERR_BLOCKED_BY_CLIENT) because they match generic CMP filters.
 *
 * Gates PostHog via localStorage `fcp_analytics_consent`.
 */
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { initAnalytics } from '../lib/analytics';
import { WWW_TAP_TARGET_CLASS } from '../lib/wwwTapTarget';
import { WWW_MOBILE_NAV_BAR_ATTR } from '../lib/wwwMobileNav';

export const CONSENT_KEY = 'fcp_analytics_consent';
export const CONSENT_GRANTED = 'true';
export const CONSENT_DECLINED = 'false';
export const CONSENT_BANNER_DELAY_MS = 1500;
export const CONSENT_BANNER_ATTR = 'data-consent-banner';
export const CONSENT_PLACEMENT_ATTR = 'data-consent-placement';
export const CONSENT_BANNER_OFFSET_VAR = '--consent-banner-offset';
export const CONSENT_BANNER_TOP_VAR = '--consent-banner-top';
export const LANDING_NAV_ATTR = 'data-landing-nav';
/** Below landing nav (z-50) and modal overlays (z-index 100) so the banner cannot cover them. */
export const CONSENT_BANNER_Z_CLASS = 'z-40';

/**
 * Auth password forms keep the banner under the nav (same as marketing hero)
 * so a bottom card cannot sit on the password row at Scout 390 / desktop.
 */
export function isAuthPasswordConsentRoute(pathname = '') {
  if (pathname === '/login' || pathname === '/register') return true;
  return pathname === '/reset-password' || pathname.startsWith('/reset-password/');
}

/**
 * Hero pages and auth password routes keep the banner under the nav so it
 * cannot sit on primary CTAs or password fields.
 * In-app pages keep a compact bottom card.
 * @param {boolean} hasMarketingHero
 * @param {string} [pathname]
 * @returns {'top' | 'bottom'}
 */
export function resolveConsentBannerPlacement(hasMarketingHero, pathname) {
  if (hasMarketingHero || isAuthPasswordConsentRoute(pathname)) {
    return 'top';
  }
  return 'bottom';
}

export function readAnalyticsConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
}

export function hasResolvedAnalyticsConsent() {
  const value = readAnalyticsConsent();
  return value === CONSENT_GRANTED || value === CONSENT_DECLINED;
}

export function persistAnalyticsConsent(granted) {
  try {
    localStorage.setItem(CONSENT_KEY, granted ? CONSENT_GRANTED : CONSENT_DECLINED);
  } catch {
    // Safari private mode / blocked storage: still hide the banner this session.
  }
}

export function resolveConsentBannerShellClass(placement) {
  return placement === 'top'
    ? `pointer-events-none fixed left-0 right-0 ${CONSENT_BANNER_Z_CLASS} px-3 pt-2 sm:px-4 top-[var(--consent-banner-top,3.5rem)]`
    : `pointer-events-none fixed bottom-0 left-0 right-0 ${CONSENT_BANNER_Z_CLASS} p-4 sm:p-6`;
}

export function measureLandingNavTopOffset() {
  // Measure the header bar only. The hamburger overlay is a sibling and must
  // not grow --consent-banner-top or shift the page when the menu opens.
  // Nav lives outside this tree; querySelector is the only way to clear the real header height.
  const bar = document.querySelector(`[${WWW_MOBILE_NAV_BAR_ATTR}]`);
  const nav = document.querySelector(`[${LANDING_NAV_ATTR}]`);
  const el = bar || nav;
  if (!el) return '3.5rem';
  const height = Math.ceil(el.getBoundingClientRect().height);
  return height > 0 ? `${height}px` : '3.5rem';
}

function PrivacyBanner() {
  const { pathname } = useLocation();
  const panelRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [placement, setPlacement] = useState('bottom');

  useEffect(() => {
    if (hasResolvedAnalyticsConsent()) {
      setVisible(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      if (!hasResolvedAnalyticsConsent()) {
        setVisible(true);
      }
    }, CONSENT_BANNER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [pathname]);

  useLayoutEffect(() => {
    const hasHero = !!document.querySelector('[data-section="hero"]');
    setPlacement(resolveConsentBannerPlacement(hasHero, pathname));
  }, [pathname]);

  useLayoutEffect(() => {
    if (!visible) {
      document.documentElement.removeAttribute(CONSENT_BANNER_ATTR);
      document.documentElement.removeAttribute(CONSENT_PLACEMENT_ATTR);
      document.documentElement.style.removeProperty(CONSENT_BANNER_OFFSET_VAR);
      document.documentElement.style.removeProperty(CONSENT_BANNER_TOP_VAR);
      return undefined;
    }
    document.documentElement.setAttribute(CONSENT_BANNER_ATTR, 'open');
    document.documentElement.setAttribute(CONSENT_PLACEMENT_ATTR, placement);
    if (placement === 'top') {
      document.documentElement.style.setProperty(
        CONSENT_BANNER_TOP_VAR,
        measureLandingNavTopOffset(),
      );
    } else {
      document.documentElement.style.removeProperty(CONSENT_BANNER_TOP_VAR);
    }
    const applyOffset = () => {
      const height = panelRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty(
        CONSENT_BANNER_OFFSET_VAR,
        `${height}px`,
      );
    };
    applyOffset();
    const node = panelRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return () => {
        document.documentElement.removeAttribute(CONSENT_BANNER_ATTR);
        document.documentElement.removeAttribute(CONSENT_PLACEMENT_ATTR);
        document.documentElement.style.removeProperty(CONSENT_BANNER_OFFSET_VAR);
        document.documentElement.style.removeProperty(CONSENT_BANNER_TOP_VAR);
      };
    }
    const observer = new ResizeObserver(applyOffset);
    observer.observe(node);
    return () => {
      observer.disconnect();
      document.documentElement.removeAttribute(CONSENT_BANNER_ATTR);
      document.documentElement.removeAttribute(CONSENT_PLACEMENT_ATTR);
      document.documentElement.style.removeProperty(CONSENT_BANNER_OFFSET_VAR);
      document.documentElement.style.removeProperty(CONSENT_BANNER_TOP_VAR);
    };
  }, [visible, placement]);

  const handleAccept = () => {
    persistAnalyticsConsent(true);
    initAnalytics();
    setVisible(false);
  };

  const handleDecline = () => {
    persistAnalyticsConsent(false);
    setVisible(false);
  };

  const shellClass = resolveConsentBannerShellClass(placement);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: placement === 'top' ? -80 : 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: placement === 'top' ? -80 : 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className={shellClass}
          data-testid="analytics-consent-banner"
          data-consent-placement={placement}
        >
          <div
            ref={panelRef}
            className="pointer-events-auto surface-panel mx-auto max-w-lg shadow-xl"
            role="region"
            aria-label="Analytics preferences"
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-stone-100">
                <Cookie className="h-5 w-5 text-stone-600" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-stone-900">Analytics preferences</h3>
                <p className="mt-1 text-xs leading-relaxed text-stone-500">
                  We use analytics to understand how you use FuelCyclePro and improve the
                  experience. No advertising or third-party tracking.
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleAccept}
                    data-www-control="cookie-accept"
                    className={`${WWW_TAP_TARGET_CLASS} inline-flex items-center justify-center rounded-lg bg-stone-900 px-4 text-xs font-semibold text-white transition-colors hover:bg-stone-800`}
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    onClick={handleDecline}
                    data-www-control="cookie-decline"
                    className={`${WWW_TAP_TARGET_CLASS} inline-flex items-center justify-center rounded-lg bg-stone-100 px-4 text-xs font-semibold text-stone-700 transition-colors hover:bg-stone-200`}
                  >
                    Decline
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={handleDecline}
                data-www-control="cookie-dismiss"
                className={`${WWW_TAP_TARGET_CLASS} inline-flex flex-shrink-0 items-center justify-center text-stone-400 transition-colors hover:text-stone-600`}
                aria-label="Dismiss analytics banner"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default PrivacyBanner;
